import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react';
import { PortalHeader, SignOutMenu } from '@cca/ui';
import { api, setToken, clearToken, getToken, type MeResponse } from './api/client';
import { Explorer } from './Explorer';
import { AdminPage } from './AdminPage';
import { FinancialStory } from './pages/FinancialStory';

type Boot = 'loading' | 'ready' | 'error';

export function App(): JSX.Element {
  const [boot, setBoot] = useState<Boot>('loading');
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [view, setView] = useState<'explore' | 'admin'>('explore');

  const refresh = useCallback(async () => {
    try {
      const state = await api.state();
      setOpen(state.open);
      if (getToken()) {
        try {
          setMe(await api.me());
        } catch {
          clearToken();
          setMe(null);
        }
      }
      setBoot('ready');
    } catch {
      setBoot('error');
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onExpired = (): void => setMe(null);
    window.addEventListener('model:session-expired', onExpired);
    return () => window.removeEventListener('model:session-expired', onExpired);
  }, [refresh]);

  const signOut = async (): Promise<void> => {
    await api.logout().catch(() => {});
    clearToken();
    setMe(null);
    setShowLogin(false);
  };

  // "Sign out all devices" lives by the Sign out pill (account plumbing, not
  // work); the confirm dialog carries the explanation.
  const signOutAll = async (): Promise<void> => {
    const message =
      'Sign out all devices?\n\nThis ends every signed-in session for your account on this calculator — every device, including this one. Other devices are signed out within about a minute. You can sign straight back in.';
    if (!window.confirm(message)) return;
    try {
      await api.logoutAll();
      clearToken();
      setMe(null);
      setShowLogin(false);
    } catch {
      window.alert('Could not sign out all devices. Please try again.');
    }
  };

  if (boot === 'loading') return <Centered>Loading…</Centered>;
  if (boot === 'error')
    return <Centered>Couldn’t reach the model. Please try again later.</Centered>;

  // Public landing for everyone not signed in: the Diocese Financial Story.
  // A quiet "Sign in" entry opens the OTP flow (works whether the model is open
  // or closed — the registrar always needs a way in). Independent of `open`.
  if (!me) {
    return showLogin ? (
      <Shell>
        <LoginPanel onAuthed={refresh} />
      </Shell>
    ) : (
      <FinancialStory onSignIn={() => setShowLogin(true)} />
    );
  }

  // Signed in but the model is closed and they're not an admin → inert screen.
  const closedForUser = !open && me.role !== 'admin';
  if (closedForUser) {
    return (
      <Shell email={me.email} onSignOut={signOut} onSignOutAll={() => void signOutAll()}>
        <ClosedScreen />
      </Shell>
    );
  }
  if (me.needsIdentity) {
    return (
      <Shell email={me.email} onSignOut={signOut} onSignOutAll={() => void signOutAll()}>
        <IdentityPanel onSaved={refresh} />
      </Shell>
    );
  }
  const isAdmin = me.role === 'admin';
  return (
    <Shell
      email={me.email}
      onSignOut={signOut}
      onSignOutAll={() => void signOutAll()}
      tabs={
        isAdmin ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Tab active={view === 'explore'} onClick={() => setView('explore')}>
              Explore
            </Tab>
            <Tab active={view === 'admin'} onClick={() => setView('admin')}>
              Admin
            </Tab>
          </div>
        ) : undefined
      }
    >
      {isAdmin && view === 'admin' ? <AdminPage /> : <Explorer />}
    </Shell>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? '#fff' : 'transparent',
        color: active ? '#005487' : 'var(--color-on-strip, #fff)',
        border: 'none',
        borderRadius: '6px 6px 0 0',
        padding: '8px 18px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

// ── Layout ─────────────────────────────────────────────────────────────────
function Shell({
  children,
  email,
  onSignOut,
  onSignOutAll,
  tabs,
}: {
  children: React.ReactNode;
  email?: string;
  onSignOut?: () => void;
  onSignOutAll?: () => void;
  tabs?: ReactNode;
}): JSX.Element {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper-cream, #fdfbf6)' }}>
      <PortalHeader
        portalName="Quota Model"
        subNav={tabs}
        rightSlot={
          email ? (
            <>
              {/* A fragment, not a row of our own: PortalHeader owns the slot
                  layout, so Sign out stays right-aligned and its menu opens on
                  screen (AUTH-1). A long address breaks anywhere rather than
                  pushing past the screen edge on a narrow phone (SC 1.4.10, cca-69gs). */}
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--color-on-strip, #fff)',
                  overflowWrap: 'anywhere',
                }}
              >
                {email}
              </span>
              {onSignOut && onSignOutAll && (
                <SignOutMenu onSignOut={onSignOut} onSignOutAll={onSignOutAll} />
              )}
            </>
          ) : undefined
        }
      />
      <main style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 60px' }}>
        {children}
      </main>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', color: '#5c4f33' }}>
      {children}
    </div>
  );
}

function ClosedScreen(): JSX.Element {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '70vh', textAlign: 'center' }}>
      <div style={{ maxWidth: 460 }}>
        <h1 style={{ color: '#005487', fontSize: 22, marginBottom: 8 }}>Model is closed</h1>
        <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
          The Quota policy model isn’t open at the moment. If a window has been announced, please
          check back then.
        </p>
      </div>
    </div>
  );
}

// ── Login (OTP) ───────────────────────────────────────────────────────────
function LoginPanel({ onAuthed }: { onAuthed: () => void }): JSX.Element {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const request = async (): Promise<void> => {
    setBusy(true);
    setErr('');
    try {
      await api.authRequestOtp(email.trim());
      setStep('code');
    } catch (e) {
      setErr(e instanceof Error ? humanize(e.message) : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };
  const verify = async (): Promise<void> => {
    setBusy(true);
    setErr('');
    try {
      const r = await api.authVerifyOtp(email.trim(), code.trim());
      setToken(r.token);
      onAuthed();
    } catch (e) {
      setErr(e instanceof Error ? humanize(e.message) : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Sign in to explore">
      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
        Enter your email and we’ll send you a one-time code. Anyone may take part in the model.
      </p>
      {step === 'email' ? (
        <>
          <input
            style={input}
            type="email"
            placeholder="you@example.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && email && void request()}
          />
          <button style={btnPrimary} disabled={busy || !email} onClick={() => void request()}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </>
      ) : (
        <>
          <input
            style={input}
            inputMode="numeric"
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && code && void verify()}
          />
          <button
            style={btnPrimary}
            disabled={busy || code.length < 6}
            onClick={() => void verify()}
          >
            {busy ? 'Verifying…' : 'Verify & continue'}
          </button>
          <button style={linkBtn} onClick={() => setStep('email')}>
            Use a different email
          </button>
        </>
      )}
      {err && <p style={{ color: '#c62828', fontSize: 13, marginTop: 12 }}>{err}</p>}
    </Card>
  );
}

function IdentityPanel({ onSaved }: { onSaved: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [church, setChurch] = useState('');
  const [org, setOrg] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const body: { displayName: string; church: string; organisation?: string } = {
        displayName: name.trim(),
        church: church.trim(),
      };
      if (org.trim()) body.organisation = org.trim();
      await api.saveIdentity(body);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Tell us who you are">
      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
        So we can attribute your feedback during the model.
      </p>
      <input
        style={input}
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        style={input}
        placeholder="Your church"
        value={church}
        onChange={(e) => setChurch(e.target.value)}
      />
      <input
        style={input}
        placeholder="Role / organisation (optional)"
        value={org}
        onChange={(e) => setOrg(e.target.value)}
      />
      <button
        style={btnPrimary}
        disabled={busy || !name.trim() || !church.trim()}
        onClick={() => void save()}
      >
        {busy ? 'Saving…' : 'Continue'}
      </button>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        maxWidth: 420,
        margin: '40px auto',
        background: '#fff',
        padding: 28,
        borderRadius: 10,
        boxShadow: '0 2px 10px rgba(0,0,0,.06)',
      }}
    >
      <h1 style={{ color: '#005487', fontSize: 20, marginBottom: 14 }}>{title}</h1>
      {children}
    </div>
  );
}

function humanize(code: string): string {
  switch (code) {
    case 'rate_limited':
      return 'Too many attempts. Please try again in a little while.';
    case 'invalid_code':
    case 'expired':
      return 'That code is wrong or expired. Request a new one.';
    case 'invalid_email':
      return 'Please enter a valid email address.';
    case 'email_failed':
      return 'We couldn’t send the email. Please try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  marginBottom: 12,
  border: '1px solid #d8d2c4',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const btnPrimary: React.CSSProperties = {
  width: '100%',
  padding: '10px 16px',
  background: '#85754d',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#005487',
  fontSize: 13,
  cursor: 'pointer',
  marginTop: 10,
  fontFamily: 'inherit',
};
